import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  ApiChallenge,
  ApiCreateChallengePayload,
  ApiUpdateChallengePayload,
  ApiChallengeEntry,
  ApiChallengeEntryDetail,
  ApiChallengeInline,
} from "griptonite-api-client";

import api from "@/core/api";
import queryClient from "@/core/queryClient";

export const useCurrentChallengeUuid = () => {
  return useParams({ strict: false }).challengeUuid;
};

export const useCreateChallengeMutation = () => {
  return useMutation({
    mutationFn: (payload: ApiCreateChallengePayload) => api.challenges.createChallenge(payload),
    onSuccess: (challenge, variables) => {
      localStorage.setItem(`challenges:${challenge.uuid}:hideWarnings:rules`, "true");
      localStorage.setItem(`challenges:${challenge.uuid}:hideWarnings:promotion`, "true");

      // Update the venue challenges cache
      const venueUuid = variables.venues[0];
      queryClient.setQueryData<ApiChallenge[]>(["venues", venueUuid, "challenges"], (old) => {
        if (!old) return [challenge];
        return [challenge, ...old];
      });

      // Set the individual challenge cache
      queryClient.setQueryData(["challenges", challenge.uuid], challenge);
    },
  });
};

export const useUpdateChallengeMutation = (challengeUuid?: string, venueUuid?: string) => {
  return useMutation({
    mutationFn: (payload: ApiUpdateChallengePayload) =>
      api.challenges.updateChallenge(challengeUuid!, payload),
    onSuccess: (challenge) => {
      // Update the venue challenges cache
      if (venueUuid) {
        queryClient.setQueryData<ApiChallenge[]>(["venues", venueUuid, "challenges"], (old) => {
          if (!old) return [challenge];
          return old.map((c) => (c.uuid === challenge.uuid ? challenge : c));
        });
      }

      // Update the individual challenge cache
      queryClient.setQueryData(["challenges", challenge.uuid], challenge);
    },
  });
};

export const useDeleteChallengeMutation = (venueUuid?: string) => {
  return useMutation({
    mutationFn: (challengeUuid: string) => api.challenges.deleteChallenge(challengeUuid),
    onSuccess: (_, challengeUuid) => {
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "challenges"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid],
      });
    },
  });
};

export const useVenueChallengesQuery = <TData = ApiChallengeInline[]>(
  venueUuid?: string,
  select?: (data: ApiChallengeInline[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", venueUuid, "challenges"],
    queryFn: async () => {
      const result = await api.venues.getVenueChallenges(venueUuid!);
      return result.data.filter((x) => x.canAdmin);
    },
    enabled: !!venueUuid,
    select,
  });
};

const findChallengeInCache = (venueUuid: string, uuid: string) => {
  const matchingQueries = queryClient.getQueriesData<ApiChallenge[]>({
    queryKey: ["venues", venueUuid, "challenges"],
  });
  for (const [_, data] of matchingQueries) {
    if (data) {
      const challenge = data.find((challenge: ApiChallenge) => challenge.uuid === uuid);
      if (challenge) {
        return challenge;
      }
    }
  }
  return undefined;
};

export const useChallengeQuery = (challengeUuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["challenges", challengeUuid],
    queryFn: () =>
      api.challenges.getChallenge(challengeUuid!) as Promise<ApiChallenge & Partial<ApiChallenge>>,
    enabled: !!challengeUuid,
    placeholderData: () => {
      return findChallengeInCache(venueUuid!, challengeUuid!) as
        | (ApiChallenge & Partial<ApiChallenge>)
        | undefined;
    },
  });
};

export const useChallengeEntries = (challengeUuid: string) => {
  return useQuery({
    queryKey: ["challenges", challengeUuid, "entries"],
    queryFn: () => api.challenges.getChallengeEntries(challengeUuid!),
    enabled: !!challengeUuid,
  });
};

export const useChallengeEntry = (challengeUuid?: string, entryUuid?: string) => {
  return useQuery({
    queryKey: ["challenges", challengeUuid, "entries", entryUuid],
    queryFn: () =>
      api.challenges.getChallengeEntry(challengeUuid!, entryUuid!) as Promise<
        Partial<ApiChallengeEntryDetail> & ApiChallengeEntry
      >,
    enabled: !!(challengeUuid && entryUuid),
    placeholderData: () => {
      const entries = queryClient.getQueryData<ApiChallengeEntry[]>([
        "challenges",
        challengeUuid,
        "entries",
      ]);
      return entries?.find((entry) => entry.uuid === entryUuid);
    },
  });
};

export const useChallengeEntryLogs = (challengeUuid?: string, entryUuid?: string) => {
  return useInfiniteQuery({
    queryKey: ["challenges", challengeUuid, entryUuid, "logs"],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const searchParams = new URLSearchParams();
      if (pageParam) {
        // Extract the page parameter from the next URL
        const url = new URL(pageParam);
        const page = url.searchParams.get("page");
        if (page) {
          searchParams.set("page", page);
        }
      }
      return await api.challenges.getChallengeEntryLogs(challengeUuid!, entryUuid!, searchParams);
    },
    enabled: !!(entryUuid && challengeUuid),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.metadata.next,
    getPreviousPageParam: (firstPage) => firstPage.metadata.previous,
  });
};

export const useManuallyVerifyChallengeEntryLogMutation = (
  challengeUuid?: string,
  entryUuid?: string,
) => {
  return useMutation({
    mutationFn: (logUuid: string) =>
      api.challenges.manuallyVerifyChallengeEntryLog(challengeUuid!, entryUuid!, logUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, entryUuid, "logs"],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries", entryUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries"],
      });
    },
  });
};

export const useRemoveChallengeEntryLogMutation = (challengeUuid?: string, entryUuid?: string) => {
  return useMutation({
    mutationFn: (logUuid: string) =>
      api.challenges.removeChallengeEntryLog(challengeUuid!, entryUuid!, logUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, entryUuid, "logs"],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries", entryUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries"],
      });
    },
  });
};

export const useRecordChallengeRedemptionMutation = (
  challengeUuid?: string,
  entryUuid?: string,
) => {
  return useMutation({
    mutationFn: (rewardUuid: string) =>
      api.challenges.recordChallengeRedemption(challengeUuid!, entryUuid!, rewardUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries", entryUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries"],
      });
    },
  });
};

export const useDeleteRewardRedemptionMutation = (challengeUuid?: string, entryUuid?: string) => {
  return useMutation({
    mutationFn: (rewardUuid: string) =>
      api.challenges.deleteRewardRedemption(challengeUuid!, entryUuid!, rewardUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries", entryUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["challenges", challengeUuid, "entries"],
      });
    },
  });
};
